"use client";

import { useEffect, useState } from "react";
import { Menu, Phone, MessageCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-5 w-16 bg-secondary animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-secondary animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-secondary animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-secondary animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-secondary animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-3 py-2 text-foreground hover:text-primary hover:bg-transparent focus:bg-transparent text-[13px] font-mono tracking-[0.15em] uppercase transition-colors">
                <span className="relative">
                  {item.label}
                  <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-12">
        <div className="h-10 w-3/4 bg-secondary animate-pulse rounded-md" />
        <div className="h-10 w-2/3 bg-secondary animate-pulse rounded-md" />
        <div className="h-10 w-3/4 bg-secondary animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col mt-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-t border-border last:border-b">
          <Link to={item.href || "#"} onClick={onNavigate} className="flex items-baseline gap-4 py-6 text-foreground hover:text-primary transition-colors">
            <span className="font-mono text-xs text-muted-foreground tracking-wider">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="font-default text-[2.5rem] leading-none">
              {item.label}
            </span>
          </Link>
        </div>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="4560" id="header" className={`sticky top-0 z-50 w-full bg-background text-foreground border-b transition-all duration-300 ${scrolled ? "bg-background/95 backdrop-blur-md border-border shadow-lg" : "border-border"}`}>
      <div className="w-full px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-2 md:grid-cols-3 items-center h-16 md:h-20">
          {}
          <div className="flex items-center justify-start">
            <Link to="/" className="flex items-center" aria-label="Hard Rock Tattoo home">
              <span className="font-serif text-xl font-bold tracking-tight uppercase whitespace-nowrap">Hard Rock Tattoo</span>
            </Link>
          </div>

          {}
          <div className="hidden md:flex items-center justify-center">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center justify-end gap-3">
            <div className="hidden md:flex items-center gap-3">
              <Button asChild variant="default" className="h-10 px-6 bg-primary border-none hover:bg-primary/90 text-primary-foreground font-default text-[12px] uppercase tracking-[0.1em] rounded-md shadow-md">
                <Link to="https://instagram.com/hardrockcytattoo" newTab>
                  <span>Instagram</span>
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-10 px-4 bg-secondary border border-border hover:bg-accent hover:text-foreground text-foreground font-default text-[12px] uppercase tracking-[0.1em] rounded-md shadow-md">
                <Link to="https://wa.me/35794043514" newTab aria-label="WhatsApp">
                  <MessageCircle className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-10 px-4 bg-secondary border border-border hover:bg-accent hover:text-foreground text-foreground font-default text-[12px] uppercase tracking-[0.1em] rounded-md shadow-md">
                <Link to="tel:+35794043514" aria-label="Call Now">
                  <Phone className="size-4" />
                </Link>
              </Button>
            </div>

            {}
            <div className="md:hidden">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-foreground hover:bg-secondary rounded-md" aria-label="Open menu">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-background text-foreground border-l border-border w-full sm:max-w-full p-6 sm:p-8 overflow-y-auto">
                  <SheetTitle className="sr-only">Navigation</SheetTitle>

                  <div className="flex items-center justify-between pt-1">
                    <Link to="/" onClick={() => setMobileOpen(false)} aria-label="Hard Rock Tattoo home">
                      <span className="font-serif text-2xl font-bold tracking-tight uppercase whitespace-nowrap">Hard Rock Tattoo</span>
                    </Link>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary">
                      Menu
                    </span>
                  </div>

                  <MenuProvider menu_id="16">
                    <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                  </MenuProvider>

                  <div className="mt-10 flex flex-col gap-4">
                    <Button asChild variant="default" className="w-full h-14 bg-primary border-none hover:bg-primary/90 text-primary-foreground font-default text-[14px] uppercase tracking-[0.12em] rounded-md" onClick={() => setMobileOpen(false)}>
                      <Link to="https://instagram.com/hardrockcytattoo" newTab>
                        <span>Instagram</span>
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="w-full h-14 bg-secondary border border-border hover:bg-accent text-foreground font-default text-[14px] uppercase tracking-[0.12em] rounded-md flex items-center justify-center gap-3" onClick={() => setMobileOpen(false)}>
                      <Link to="https://wa.me/35794043514" newTab>
                        <MessageCircle className="size-5" />
                        <span>WhatsApp</span>
                      </Link>
                    </Button>
                    <Button asChild variant="outline" className="w-full h-14 bg-secondary border border-border hover:bg-accent text-foreground font-default text-[14px] uppercase tracking-[0.12em] rounded-md flex items-center justify-center gap-3" onClick={() => setMobileOpen(false)}>
                      <Link to="tel:+35794043514">
                        <Phone className="size-5" />
                        <span>Call Now</span>
                      </Link>
                    </Button>
                  </div>

                  <div className="mt-10 pt-6 border-t border-border">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                      Paphos, Cyprus
                    </p>
                    <p className="mt-2 font-default text-sm text-foreground/80">
                      masaranitattoo@gmail.com
                    </p>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </section>;
}